'use client';

import { use, useMemo, useState } from 'react';
import {
    getSchoolData,
    getSchoolName,
    SchoolUnit,
    PettyCashRequest,
    validUnits
} from '@/lib/data';
import {
    Building2,
    Search,
    ChevronDown,
    ChevronRight,
    CreditCard,
    Info,
    X,
    Filter,
    Calendar,
    ArrowUpCircle,
    CheckCircle2,
    Clock,
    UserCircle,
    Eye,
    Ban,
    Wallet
} from 'lucide-react';
import Link from 'next/link';
import { notFound } from 'next/navigation';

export default function PettyCashPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit: initialUnit } = use(params);

    if (!validUnits.includes(initialUnit as SchoolUnit)) {
        notFound();
    }

    const baseType = initialUnit.split('-')[0];
    const sisterUnits = validUnits.filter(u =>
        u.startsWith(baseType) &&
        (u.includes('-') || !validUnits.some(v => v.startsWith(baseType) && v.includes('-')))
    );

    const defaultUnit = initialUnit.includes('-')
        ? initialUnit as SchoolUnit
        : (sisterUnits.find(u => u.includes('-')) || initialUnit) as SchoolUnit;

    const [activeUnit, setActiveUnit] = useState<SchoolUnit>(defaultUnit);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [selectedRequest, setSelectedRequest] = useState<PettyCashRequest | null>(null);

    const schoolData = getSchoolData(activeUnit);
    const schoolName = getSchoolName(activeUnit);

    const camelCaseUnit = activeUnit.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    const requests = (
        (schoolData as any)[`${camelCaseUnit}PettyCash`] ||
        (schoolData as any)[`${activeUnit.split('-')[0]}PettyCash`] ||
        []
    ) as PettyCashRequest[];

    const filteredRequests = useMemo(() => {
        return requests.filter(r => {
            const matchesSearch = (r.applicant || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (r.purpose || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === 'All' || r.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [requests, searchTerm, statusFilter]);

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header Area */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="relative z-10 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                                <Link href={`/yayasan/${initialUnit}/keuangan`} className="hover:text-primary transition-all">Keuangan</Link>
                                <ChevronRight className="w-3.5 h-3.5" />
                                <span className="text-foreground">Permintaan Kas Laci</span>
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 rounded-2xl bg-indigo-soft/50 border border-indigo-10 flex items-center justify-center text-indigo-600 shadow-inner">
                                    <Wallet className="w-8 h-8" />
                                </div>
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight whitespace-nowrap">Permintaan Kas Laci</h1>
                                    <p className="text-secondary font-medium flex items-center gap-2">
                                        <Building2 className="w-3.5 h-3.5 text-indigo-500" /> Kelola Petty Cash Unit {baseType.toUpperCase()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Unit Selector */}
                        <div className="relative min-w-[280px]">
                            <label className="text-[10px] font-bold text-muted uppercase tracking-widest mb-2 block ml-1">Pilih Unit Sekolah</label>
                            <div className="relative group">
                                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                                <select
                                    className="w-full pl-11 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-sm font-bold focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all appearance-none cursor-pointer outline-none shadow-sm"
                                    value={activeUnit}
                                    onChange={(e) => setActiveUnit(e.target.value as SchoolUnit)}
                                >
                                    {sisterUnits.map(u => (
                                        <option key={u} value={u}>{getSchoolName(u as SchoolUnit)}</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Table Container */}
            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden flex flex-col min-h-[400px]">
                <div className="p-6 border-b border-border-main flex flex-col md:flex-row md:items-center justify-between gap-4 bg-background/30">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari pengaju atau tujuan penggunaan..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-2xl text-sm focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Tgl Permintaan</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Pengaju</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Nominal</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Tujuan Penggunaan</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Status</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main font-medium">
                            {filteredRequests.length > 0 ? filteredRequests.map((req) => (
                                <tr key={req.id} className="hover:bg-indigo-soft/10 transition-colors group text-sm">
                                    <td className="px-8 py-5 whitespace-nowrap text-secondary font-bold tabular-nums">
                                        {req.date}
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-background border border-border-main flex items-center justify-center">
                                                <UserCircle className="w-5 h-5 text-muted" />
                                            </div>
                                            <span className="font-bold text-foreground">{req.applicant}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 text-right whitespace-nowrap font-bold text-primary tabular-nums">
                                        {req.amount}
                                    </td>
                                    <td className="px-8 py-5">
                                        <p className="text-secondary truncate max-w-[200px]">{req.purpose}</p>
                                    </td>
                                    <td className="px-8 py-5 text-center">
                                        <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider ${req.status === 'approved' ? 'bg-success-soft text-success' :
                                            req.status === 'pending' ? 'bg-warning-soft text-warning' : 'bg-error-soft text-error'
                                            }`}>
                                            {req.status === 'approved' ? 'Disetujui' :
                                                req.status === 'pending' ? 'Tertunda' : 'Ditolak'}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5 text-right">
                                        <div className="flex items-center justify-end gap-2 text-[10px] font-bold uppercase tracking-widest">
                                            {req.status === 'pending' && (
                                                <>
                                                    <button className="px-3 py-1.5 bg-success text-white rounded-lg hover:bg-success-dark transition-all">Setuju</button>
                                                    <button className="px-3 py-1.5 bg-error text-white rounded-lg hover:bg-error-dark transition-all">Tolak</button>
                                                </>
                                            )}
                                            <button
                                                onClick={() => setSelectedRequest(req)}
                                                className="p-2 rounded-xl bg-background border border-border-main text-muted hover:text-primary transition-all ml-2"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={6} className="px-8 py-32 text-center text-muted">
                                        <div className="flex flex-col items-center gap-4 opacity-10">
                                            <Wallet className="w-16 h-16" />
                                            <p className="font-bold text-sm uppercase tracking-widest">Tidak ada permintaan kas laci</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Detail Modal */}
            {selectedRequest && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setSelectedRequest(null)}></div>
                    <div className="relative bg-surface w-full max-w-lg rounded-4xl border border-border-main shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-border-main flex items-center justify-between bg-background/50">
                            <div className="flex items-center gap-5">
                                <div className="w-14 h-14 rounded-2xl bg-indigo-soft/50 border border-indigo-10 flex items-center justify-center text-indigo-600">
                                    <Wallet className="w-7 h-7" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-foreground">Detail Permintaan</h3>
                                    <p className="text-secondary text-xs font-medium uppercase tracking-widest mt-0.5">Petty Cash Request</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedRequest(null)} className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-foreground hover:rotate-90 transition-all duration-300">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-8 space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Tgl Permintaan</p>
                                    <p className="text-sm font-bold text-foreground">{selectedRequest.date}</p>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Status</p>
                                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase text-warning">
                                        <Clock className="w-3.5 h-3.5" /> {selectedRequest.status}
                                    </span>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Nama Pengaju</p>
                                    <p className="text-sm font-bold text-foreground">{selectedRequest.applicant}</p>
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Unit Sekolah</p>
                                    <p className="text-sm font-bold text-foreground">{schoolName}</p>
                                </div>
                            </div>

                            <div className="p-6 bg-background rounded-3xl border border-border-main">
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Nominal Diminta</p>
                                <p className="text-3xl font-bold text-foreground tabular-nums tracking-tight">{selectedRequest.amount}</p>
                            </div>

                            <div className="space-y-2">
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Tujuan Penggunaan</p>
                                <div className="p-4 bg-background border border-border-main rounded-2xl text-xs font-medium text-secondary leading-relaxed italic">
                                    "{selectedRequest.purpose}"
                                </div>
                            </div>
                        </div>

                        <div className="p-8 bg-background/50 border-t border-border-main flex justify-end gap-3">
                            <button onClick={() => setSelectedRequest(null)} className="px-8 py-3.5 bg-surface border border-border-main text-secondary rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:bg-background transition-all">Tutup</button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
